import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { Sparkline } from '../../components/Sparkline';
import { MetricCard } from '../../components/Card';
import type { ClickEvent, LinkAnalytics } from '@shared/types';
import { formatNumber, formatRelative } from '../../lib/format';

export function AdminAnalyticsPage() {
  const [data, setData] = useState<LinkAnalytics | null>(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<LinkAnalytics>(`/api/admin/analytics?days=${days}`)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [days]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Platform analytics</h1>
          <p className="text-sm text-ink-500 dark:text-ink-400">
            Aggregate click activity across every organization.
          </p>
        </div>
        <select
          className="input max-w-[160px]"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </select>
      </header>
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {!data && (
        <div className="flex items-center justify-center py-16">
          <Spinner size={28} />
        </div>
      )}
      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <MetricCard label="Successful clicks" value={formatNumber(data.totals.total)} />
            <MetricCard label="Qualified clicks" value={formatNumber(data.totals.qualified)} />
            <MetricCard label="Unique visitors" value={formatNumber(data.totals.unique)} />
            <MetricCard label="Bot / scanner" value={formatNumber(data.totals.bot)} />
            <MetricCard label="Suspicious" value={formatNumber(data.totals.suspicious)} />
            <MetricCard label="Blocked" value={formatNumber(data.totals.blocked)} />
            <MetricCard label="Expired" value={formatNumber(data.totals.expired)} />
          </div>
          <section className="card p-5">
            <Sparkline
              data={data.timeseries.map((d) => ({ date: d.date, total: d.totalClicks }))}
              height={160}
              showAxis
            />
          </section>
          <section className="grid gap-6 lg:grid-cols-2">
            <Breakdown title="Top countries" rows={data.topCountries} />
            <Breakdown title="Top states / regions" rows={data.topRegions} />
            <Breakdown title="Top cities" rows={data.topCities} />
            <Breakdown title="Top continents" rows={data.topContinents} />
            <Breakdown title="Top networks" rows={data.topNetworks} />
            <Breakdown title="Top timezones" rows={data.topTimezones} />
            <Breakdown title="Edge locations" rows={data.topColos} />
            <Breakdown title="UTM sources" rows={data.topUtmSources} />
            <Breakdown title="UTM mediums" rows={data.topUtmMediums} />
            <Breakdown title="UTM campaigns" rows={data.topUtmCampaigns} />
            <Breakdown title="Bot classifications" rows={data.topBotClassifications} />
            <Breakdown title="Bot reasons" rows={data.topBotReasons} />
            <Breakdown title="Top referrers" rows={data.topReferrers} />
            <Breakdown title="Devices" rows={data.topDevices} />
            <Breakdown title="Browsers" rows={data.topBrowsers} />
          </section>
          <section className="card p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <div>
                <h2 className="font-semibold">Recent click events</h2>
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  Shows latest events platform-wide. IP addresses are redacted before display.
                </p>
              </div>
              <span className="text-xs text-ink-500 dark:text-ink-400">
                {formatNumber(data.recent.length)} latest events
              </span>
            </div>
            {data.recent.length === 0 ? (
              <p className="text-sm text-ink-500 dark:text-ink-400">No click events yet.</p>
            ) : (
              <div className="overflow-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>Quality</th>
                      <th>Bot reason</th>
                      <th>Location</th>
                      <th>IP (redacted)</th>
                      <th>Network</th>
                      <th>Timezone</th>
                      <th>Coordinates</th>
                      <th>Edge</th>
                      <th>Device</th>
                      <th>Browser</th>
                      <th>OS</th>
                      <th>Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((event) => (
                      <tr key={event.id}>
                        <td className="text-xs whitespace-nowrap" title={event.occurredAt}>
                          {formatRelative(event.occurredAt)}
                        </td>
                        <td>
                          <span
                            className={
                              event.eventType === 'success' ? 'badge-success' : 'badge-warn'
                            }
                          >
                            {event.eventType}
                          </span>
                        </td>
                        <td>
                          <span className={event.isQualifiedClick ? 'badge-success' : 'badge-warn'}>
                            {event.botClassification}
                          </span>
                        </td>
                        <td className="text-xs whitespace-nowrap">{event.botReason ?? '—'}</td>
                        <td className="text-sm min-w-[160px]">{formatLocation(event)}</td>
                        <td className="font-mono text-xs whitespace-nowrap">
                          {event.ipAddress ?? '—'}
                        </td>
                        <td
                          className="text-xs truncate max-w-[180px]"
                          title={event.asOrganization ?? undefined}
                        >
                          {formatNetwork(event)}
                        </td>
                        <td className="text-xs whitespace-nowrap">{event.timezone ?? '—'}</td>
                        <td className="font-mono text-xs whitespace-nowrap">
                          {formatCoordinates(event)}
                        </td>
                        <td className="text-xs">{event.colo ?? '—'}</td>
                        <td>{event.deviceType ?? '—'}</td>
                        <td>{event.browser ?? '—'}</td>
                        <td>{event.os ?? '—'}</td>
                        <td className="truncate max-w-[220px]">{event.referrer ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function formatLocation(event: ClickEvent): string {
  const region = event.regionCode || event.region;
  const parts = [event.city, region, event.country]
    .filter((part): part is string => !!part && part.length > 0);
  if (parts.length === 0) return event.continent ?? '—';
  return parts.join(', ');
}

function formatNetwork(event: ClickEvent): string {
  if (event.asOrganization) return event.asOrganization;
  if (event.asn != null) return `AS${event.asn}`;
  return '—';
}

function formatCoordinates(event: ClickEvent): string {
  if (!event.latitude || !event.longitude) return '—';
  return `${event.latitude}, ${event.longitude}`;
}

function Breakdown({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; count: number }[];
}) {
  if (!rows.length) {
    return (
      <div className="card p-5">
        <h3 className="font-semibold mb-2">{title}</h3>
        <p className="text-sm text-ink-500 dark:text-ink-400">No data yet.</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="card p-5">
      <h3 className="font-semibold mb-3">{title}</h3>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.key} className="text-sm">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-medium truncate" title={r.key}>
                {r.key || '—'}
              </span>
              <span className="text-ink-500 dark:text-ink-400">
                {formatNumber(r.count)}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-ink-100 dark:bg-ink-800 overflow-hidden">
              <div
                className="h-full bg-brand-500"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
